import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { offerApi } from "api/TenantSlug/Offer/api";
import { composeGetOfferQueryOptions } from "api/TenantSlug/Offer/queryOptions.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import StoreOfferRequest = App.Http.Requests.Offer.StoreOfferRequest;
import UpdateOfferRequest = App.Http.Requests.Offer.UpdateOfferRequest;
import GetOffersRequest = App.Http.Requests.Offer.GetOffersRequest;
import GetOfferResponse = App.Http.Responses.Offer.GetOfferResponse;
import GetOffersResponse = App.Http.Responses.Offer.GetOffersResponse;
import GetOfferRequest = App.Http.Requests.Offer.GetOfferRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Query to get a list of offers
export const useGetOffersQuery = (request: GetOffersRequest, options = {}) => {
    return useQueryApi<GetOffersResponse>({
        queryKey: ["getOffers", request],
        queryFn: () => offerApi.index(request),
        ...options,
    });
};

// Query to get a specific offer
export const useGetOfferQuery = (offerId: string, request: GetOfferRequest, options = {}) => {
    return useQueryApi<GetOfferResponse>({
        ...composeGetOfferQueryOptions(offerId, request),
        ...options,
    });
};

// Mutation to store a new offer
export const useStoreOfferMutation = (
    options: UseMutationOptions<GetOfferResponse, AxiosError<ApiErrorResponse>, StoreOfferRequest> = {}
) => {
    return useMutationApi<GetOfferResponse, StoreOfferRequest>({
        mutationKey: ["storeOffer"],
        mutationFn: (request) => offerApi.store(request),
        ...options,
    });
};

// Mutation to update an existing offer
export const useUpdateOfferMutation = (offerId: string, options = {}) => {
    return useMutationApi<GetOfferResponse, UpdateOfferRequest>({
        mutationKey: ["updateOffer", offerId],
        mutationFn: (request: UpdateOfferRequest) => offerApi.update(request, offerId),
        ...options,
    });
};

// Mutation to destroy an offer
export const useDestroyOfferMutation = (offerId: string, options = {}) => {
    return useMutationApi<void, string>({
        mutationKey: ["destroyOffer", offerId],
        mutationFn: (offerId) => offerApi.destroy(offerId),
        ...options,
    });
};

export const useExportOffersMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportOffers"],
        mutationFn: (request) => offerApi.export(request),
        ...options,
    });
};
